import { LineChart, BarChart, TableChart } from "../../components/ui/Charts";
import type { ChartDatum } from "../../types/demo";

const monthlySpending: ChartDatum[] = [
  { label: "Apr", value: 42500 },
  { label: "May", value: 38200 },
  { label: "Jun", value: 45800 },
  { label: "Jul", value: 51200 },
  { label: "Aug", value: 47600 },
  { label: "Sep", value: 53100 },
  { label: "Oct", value: 49800 },
  { label: "Nov", value: 44300 },
  { label: "Dec", value: 58700 },
  { label: "Jan", value: 52400 },
  { label: "Feb", value: 48900 },
  { label: "Mar", value: 55200 },
];

const weeklyCashflow: ChartDatum[] = [
  { label: "Week 1", value: 12400 },
  { label: "Week 2", value: 18700 },
  { label: "Week 3", value: 15200 },
  { label: "Week 4", value: 22100 },
  { label: "Week 5", value: 19800 },
  { label: "Week 6", value: 24500 },
  { label: "Week 7", value: 21300 },
  { label: "Week 8", value: 28900 },
];

const revenueByClient: ChartDatum[] = [
  { label: "Riverpoint Tower", value: 185000 },
  { label: "Harbor Logistics Fitout", value: 142000 },
  { label: "Sunset Apartments Stage 2", value: 128000 },
  { label: "Central Medical Retrofit", value: 97500 },
  { label: "Metro Depot Upgrade", value: 84200 },
  { label: "Greenridge Utilities", value: 63000 },
];

const overdueByBucket: ChartDatum[] = [
  { label: "1-15 days", value: 23400 },
  { label: "16-30 days", value: 18700 },
  { label: "31-60 days", value: 34200 },
  { label: "61-90 days", value: 12800 },
  { label: "90+ days", value: 8500 },
];

const projectBudgets: ChartDatum[] = [
  { label: "Riverpoint Tower", value: 245000 },
  { label: "Harbor Logistics Fitout", value: 189000 },
  { label: "Northline Civil Package", value: 312000 },
  { label: "Central Medical Retrofit", value: 156000 },
  { label: "Metro Depot Upgrade", value: 98000 },
  { label: "Sunset Apartments Stage 2", value: 278000 },
];

const smallDataset: ChartDatum[] = [
  { label: "Revenue", value: 245000 },
  { label: "Expenses", value: 198000 },
  { label: "Profit", value: 47000 },
];

const netCashMovement: ChartDatum[] = [
  { label: "Week 1", value: -3200 },
  { label: "Week 2", value: -4100 },
  { label: "Week 3", value: -5200 },
  { label: "Week 4", value: -2791 },
];

const mixedProfitLoss: ChartDatum[] = [
  { label: "Jan", value: 12400 },
  { label: "Feb", value: -3800 },
  { label: "Mar", value: 8200 },
  { label: "Apr", value: -7100 },
  { label: "May", value: 15600 },
  { label: "Jun", value: -1200 },
];

const budgetVariance: ChartDatum[] = [
  { label: "Riverpoint Tower", value: 24000 },
  { label: "Harbor Logistics", value: -18500 },
  { label: "Northline Civil", value: -42000 },
  { label: "Central Medical", value: 8700 },
  { label: "Metro Depot", value: -5200 },
  { label: "Sunset Apts", value: 31000 },
];

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <p className="mb-4 text-sm font-semibold text-slate-700">{title}</p>
    {children}
  </div>
);

export default () => {
  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Chart Component Tests</h1>
          <p className="mt-1 text-sm text-slate-500">
            Testing the same chart components the AI renders inline via the render_chart tool call.
          </p>
        </div>

        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-800">Line Charts</h2>
          <p className="text-xs text-slate-400">Used for trends over time (cashflow, revenue, spending)</p>
        </div>

        <ChartCard title="Monthly Spending (Last 12 Months)">
          <LineChart data={monthlySpending} />
        </ChartCard>

        <ChartCard title="Weekly Net Cashflow (8 Weeks)">
          <LineChart data={weeklyCashflow} />
        </ChartCard>

        <ChartCard title="Monthly P&L (Mixed Positive/Negative)">
          <LineChart data={mixedProfitLoss} />
        </ChartCard>

        <ChartCard title="Net Cash Movement (All Negative)">
          <LineChart data={netCashMovement} />
        </ChartCard>

        <div className="space-y-3 pt-4">
          <h2 className="text-lg font-semibold text-slate-800">Bar Charts</h2>
          <p className="text-xs text-slate-400">Used for comparing categories (clients, projects, buckets)</p>
        </div>

        <ChartCard title="Revenue by Project">
          <BarChart data={revenueByClient} />
        </ChartCard>

        <ChartCard title="Overdue Invoices by Aging Bucket">
          <BarChart data={overdueByBucket} />
        </ChartCard>

        <ChartCard title="Summary (Small Dataset)">
          <BarChart data={smallDataset} />
        </ChartCard>

        <ChartCard title="Budget Variance by Project (Mixed +/-)">
          <BarChart data={budgetVariance} />
        </ChartCard>

        <ChartCard title="Net Cash Movement (All Negative)">
          <BarChart data={netCashMovement} />
        </ChartCard>

        <div className="space-y-3 pt-4">
          <h2 className="text-lg font-semibold text-slate-800">Table Charts</h2>
          <p className="text-xs text-slate-400">Used for exact figures where precision matters</p>
        </div>

        <ChartCard title="Project Budget Allocations">
          <TableChart data={projectBudgets} />
        </ChartCard>

        <ChartCard title="Monthly Spending Breakdown">
          <TableChart data={monthlySpending} />
        </ChartCard>

        <ChartCard title="Budget Variance (Mixed +/-)">
          <TableChart data={budgetVariance} />
        </ChartCard>
      </div>
    </div>
  );
};
